// src/components/workflows/views/LogsTable.jsx - Run history table for automation dashboard
import React, { useState } from 'react';
import { List, ChevronDown, ChevronRight, CheckCircle2, XCircle, Loader2, Clock } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table.tsx';

/**
 * LogsTable - Displays run history with expandable logs
 *
 * Props:
 *   runs: Array<{
 *     id, triggered_at, triggered_by, status, completed_at,
 *     result_summary, logs: Array<{ timestamp, level, message, step }>
 *   }>
 *   onLoadLogs: (runId) => void - callback to load logs for a run
 */
const LogsTable = ({ runs = [], onLoadLogs }) => {
  const [expandedRuns, setExpandedRuns] = useState(new Set());

  const toggleExpand = (runId) => {
    const newExpanded = new Set(expandedRuns);
    if (newExpanded.has(runId)) {
      newExpanded.delete(runId);
    } else {
      newExpanded.add(runId);
      // Load logs if callback provided
      onLoadLogs?.(runId);
    }
    setExpandedRuns(newExpanded);
  };

  const getStatusDisplay = (status) => {
    const configs = {
      completed: { label: 'Completed', color: 'text-emerald-500', icon: CheckCircle2 },
      failed: { label: 'Failed', color: 'text-destructive', icon: XCircle },
      running: { label: 'Running', color: 'text-primary', icon: Loader2, animate: true },
      pending: { label: 'Pending', color: 'text-amber-500', icon: Clock },
    };
    const config = configs[status] || configs.pending;
    const Icon = config.icon;

    return (
      <div className={`flex items-center gap-2 ${config.color}`}>
        <Icon className={`w-4 h-4 ${config.animate ? 'animate-spin' : ''}`} />
        <span className="text-sm font-medium">{config.label}</span>
      </div>
    );
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return '-';
    const durationMs = new Date(end) - new Date(start);
    const seconds = Math.round(durationMs / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSec = seconds % 60;
    return `${minutes}m ${remainingSec}s`;
  };

  const formatTriggeredBy = (triggeredBy) => {
    const labels = {
      schedule: 'Scheduled',
      manual: 'Manual',
      api: 'API',
    };
    return labels[triggeredBy] || triggeredBy;
  };

  const getLogLevelColor = (level) => {
    const colors = {
      error: 'text-destructive',
      warning: 'text-amber-500',
      info: 'text-muted-foreground',
      debug: 'text-muted-foreground/60',
    };
    return colors[level] || colors.info;
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
          <List className="w-5 h-5" />
          Run History
          {runs.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-normal">
              {runs.length}
            </span>
          )}
        </h3>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          Previous automation runs and their execution logs.
        </p>
      </div>

      {runs.length > 0 ? (
        <div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Triggered By</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run) => {
                const isExpanded = expandedRuns.has(run.id);
                return (
                  <React.Fragment key={run.id}>
                    {/* Main Row */}
                    <TableRow
                      className="cursor-pointer group"
                      onClick={() => toggleExpand(run.id)}
                    >
                      <TableCell className="p-2">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatTime(run.triggered_at)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatTriggeredBy(run.triggered_by)}
                      </TableCell>
                      <TableCell>
                        {getStatusDisplay(run.status)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground text-right">
                        {formatDuration(run.triggered_at, run.completed_at)}
                      </TableCell>
                    </TableRow>

                    {/* Expanded Logs Row */}
                    {isExpanded && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={5} className="p-0">
                          <div className="bg-zinc-900/50 p-4 border-t border-border">
                            {/* Result Summary */}
                            {run.result_summary && Object.keys(run.result_summary).length > 0 && (
                              <div className="mb-4 pb-4 border-b border-border">
                                <div className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Result Summary</div>
                                <div className="flex flex-wrap gap-4">
                                  {Object.entries(run.result_summary).map(([key, value]) => (
                                    <div key={key} className="text-sm">
                                      <span className="text-muted-foreground">{key.replace(/_/g, ' ')}: </span>
                                      <span className="text-foreground font-medium">{String(value)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Error Message */}
                            {run.error_message && (
                              <div className="mb-4 pb-4 border-b border-border">
                                <div className="text-xs text-destructive mb-2 uppercase tracking-wide">Error</div>
                                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg border border-destructive/20">
                                  {run.error_message}
                                </div>
                              </div>
                            )}

                            {/* Logs */}
                            <div className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Logs</div>
                            {run.logs && run.logs.length > 0 ? (
                              <div className="space-y-1 max-h-64 overflow-y-auto font-mono text-xs">
                                {run.logs.map((log, index) => (
                                  <div
                                    key={index}
                                    className="flex items-start gap-3 py-1 px-2 rounded hover:bg-zinc-800/50"
                                  >
                                    <span className="text-muted-foreground/60 w-20 flex-shrink-0">
                                      {new Date(log.timestamp).toLocaleTimeString()}
                                    </span>
                                    <span className={`w-16 flex-shrink-0 uppercase ${getLogLevelColor(log.level)}`}>
                                      [{log.level}]
                                    </span>
                                    {log.step && (
                                      <span className="text-primary w-20 flex-shrink-0">
                                        [{log.step}]
                                      </span>
                                    )}
                                    <span className="text-foreground break-all">
                                      {log.message}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-muted-foreground italic">
                                No logs available
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="p-8 border border-zinc-700/50 rounded-lg bg-zinc-900/50 text-center">
          <List className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No runs yet</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Run history will appear here</p>
        </div>
      )}
    </div>
  );
};

export default LogsTable;
